/**
 * Generated by orval v8.22.0 🍺
 * Do not edit manually.
 * AutoGPT Agent Server
 * This server is used to execute agents that are created by the AutoGPT system.
 * OpenAPI spec version: 0.1
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type { BodyUploadBrainDumpPart } from "../../models/bodyUploadBrainDumpPart.ts";

import type { DiscardBrainDumpParams } from "../../models/discardBrainDumpParams.ts";

import type { DumpStatusResponse } from "../../models/dumpStatusResponse.ts";

import type { FinalizeRequest } from "../../models/finalizeRequest.ts";

import type { FinalizeResponse } from "../../models/finalizeResponse.ts";

import type { GreetingDoneResponse } from "../../models/greetingDoneResponse.ts";

import type { HTTP401NotAuthenticatedErrorResponse } from "../../models/hTTP401NotAuthenticatedErrorResponse.ts";

import type { HTTPValidationError } from "../../models/hTTPValidationError.ts";

import type { IntroCardResponse } from "../../models/introCardResponse.ts";

import type { RecommendedExpertsResponse } from "../../models/recommendedExpertsResponse.ts";

import type { RecommendedProvidersResponse } from "../../models/recommendedProvidersResponse.ts";

import type { SuggestedTasksResponse } from "../../models/suggestedTasksResponse.ts";

import type { SuggestedTasksUpdate } from "../../models/suggestedTasksUpdate.ts";

import type { UploadPartResponse } from "../../models/uploadPartResponse.ts";

import { customMutator } from "../../../mutators/custom-mutator.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type discardBrainDumpResponse200 = {
  data: DumpStatusResponse;
  status: 200;
};

export type discardBrainDumpResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type discardBrainDumpResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type discardBrainDumpResponseSuccess = discardBrainDumpResponse200 & {
  headers: Headers;
};
export type discardBrainDumpResponseError = (
  | discardBrainDumpResponse401
  | discardBrainDumpResponse422
) & {
  headers: Headers;
};

export type discardBrainDumpResponse =
  | discardBrainDumpResponseSuccess
  | discardBrainDumpResponseError;

export const getDiscardBrainDumpUrl = (params?: DiscardBrainDumpParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/onboarding/brain-dump?${stringifiedParams}`
    : `/api/onboarding/brain-dump`;
};

/**
 * Drop a half-uploaded take's server buffer when the user re-records.
 *
 * Stored audio is left alone. A named take still uploading is also marked
 * failed, so it no longer holds the row against the user's next take, and
 * a part of it that arrives later is refused.
 *
 * ``recording_id`` says *which* take to drop. Without it the row's
 * current take is assumed, which is wrong as soon as a second tab has
 * moved the row on: the caller would clear a buffer that is still being
 * filled. Callers know their own id, so they send it.
 * @summary Discard Brain Dump
 */
export const discardBrainDump = async (
  params?: DiscardBrainDumpParams,
  options?: RequestInit,
): Promise<discardBrainDumpResponse> => {
  return customMutator<discardBrainDumpResponse>(
    getDiscardBrainDumpUrl(params),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getDiscardBrainDumpMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof discardBrainDump>>,
    TError,
    { params?: DiscardBrainDumpParams },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof discardBrainDump>>,
  TError,
  { params?: DiscardBrainDumpParams },
  TContext
> => {
  const mutationKey = ["discardBrainDump"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof discardBrainDump>>,
    { params?: DiscardBrainDumpParams }
  > = (props) => {
    const { params } = props ?? {};

    return discardBrainDump(params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DiscardBrainDumpMutationResult = NonNullable<
  Awaited<ReturnType<typeof discardBrainDump>>
>;

export type DiscardBrainDumpMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Discard Brain Dump
 */
export const useDiscardBrainDump = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof discardBrainDump>>,
      TError,
      { params?: DiscardBrainDumpParams },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof discardBrainDump>>,
  TError,
  { params?: DiscardBrainDumpParams },
  TContext
> => {
  return useMutation(getDiscardBrainDumpMutationOptions(options), queryClient);
};
export type finalizeBrainDumpResponse200 = {
  data: FinalizeResponse;
  status: 200;
};

export type finalizeBrainDumpResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type finalizeBrainDumpResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type finalizeBrainDumpResponseSuccess = finalizeBrainDumpResponse200 & {
  headers: Headers;
};
export type finalizeBrainDumpResponseError = (
  | finalizeBrainDumpResponse401
  | finalizeBrainDumpResponse422
) & {
  headers: Headers;
};

export type finalizeBrainDumpResponse =
  | finalizeBrainDumpResponseSuccess
  | finalizeBrainDumpResponseError;

export const getFinalizeBrainDumpUrl = () => {
  return `/api/onboarding/brain-dump/finalize`;
};

/**
 * Close out a take: assemble, scan, store, transcribe.
 *
 * Extraction and greeting generation continue in the background after
 * this responds — the preparing step polls ``/status`` until
 * ``greeting_ready``. Idempotent per ``recording_id`` — a client that
 * retries after a timeout gets the stored result rather than a second
 * transcription.
 * @summary Finalize Brain Dump
 */
export const finalizeBrainDump = async (
  finalizeRequest: FinalizeRequest,
  options?: RequestInit,
): Promise<finalizeBrainDumpResponse> => {
  return customMutator<finalizeBrainDumpResponse>(getFinalizeBrainDumpUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(finalizeRequest),
  });
};

export const getFinalizeBrainDumpMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof finalizeBrainDump>>,
    TError,
    { data: FinalizeRequest },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof finalizeBrainDump>>,
  TError,
  { data: FinalizeRequest },
  TContext
> => {
  const mutationKey = ["finalizeBrainDump"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof finalizeBrainDump>>,
    { data: FinalizeRequest }
  > = (props) => {
    const { data } = props ?? {};

    return finalizeBrainDump(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type FinalizeBrainDumpMutationResult = NonNullable<
  Awaited<ReturnType<typeof finalizeBrainDump>>
>;
export type FinalizeBrainDumpMutationBody = FinalizeRequest;
export type FinalizeBrainDumpMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Finalize Brain Dump
 */
export const useFinalizeBrainDump = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof finalizeBrainDump>>,
      TError,
      { data: FinalizeRequest },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof finalizeBrainDump>>,
  TError,
  { data: FinalizeRequest },
  TContext
> => {
  return useMutation(getFinalizeBrainDumpMutationOptions(options), queryClient);
};
export type getBrainDumpIntroResponse200 = {
  data: IntroCardResponse;
  status: 200;
};

export type getBrainDumpIntroResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getBrainDumpIntroResponseSuccess = getBrainDumpIntroResponse200 & {
  headers: Headers;
};
export type getBrainDumpIntroResponseError = getBrainDumpIntroResponse401 & {
  headers: Headers;
};

export type getBrainDumpIntroResponse =
  | getBrainDumpIntroResponseSuccess
  | getBrainDumpIntroResponseError;

export const getGetBrainDumpIntroUrl = () => {
  return `/api/onboarding/brain-dump/intro`;
};

/**
 * Content for the copilot home's onboarding greeting.
 *
 * Pre-generated during finalize, so this is a plain row read — the
 * copilot home never waits on a model to render its first screen.
 * ``greeting_done=true`` means the client should show nothing and cache
 * that locally.
 * @summary Get Brain Dump Intro
 */
export const getBrainDumpIntro = async (
  options?: RequestInit,
): Promise<getBrainDumpIntroResponse> => {
  return customMutator<getBrainDumpIntroResponse>(getGetBrainDumpIntroUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetBrainDumpIntroQueryKey = () => {
  return [`/api/onboarding/brain-dump/intro`] as const;
};

export const getGetBrainDumpIntroQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrainDumpIntro>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getBrainDumpIntro>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBrainDumpIntroQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBrainDumpIntro>>
  > = ({ signal }) => getBrainDumpIntro({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrainDumpIntro>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrainDumpIntroQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrainDumpIntro>>
>;
export type GetBrainDumpIntroQueryError = HTTP401NotAuthenticatedErrorResponse;

export function useGetBrainDumpIntro<
  TData = Awaited<ReturnType<typeof getBrainDumpIntro>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpIntro>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpIntro>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpIntro>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpIntro<
  TData = Awaited<ReturnType<typeof getBrainDumpIntro>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpIntro>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpIntro>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpIntro>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpIntro<
  TData = Awaited<ReturnType<typeof getBrainDumpIntro>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpIntro>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Brain Dump Intro
 */

export function useGetBrainDumpIntro<
  TData = Awaited<ReturnType<typeof getBrainDumpIntro>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpIntro>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrainDumpIntroQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Brain Dump Intro
 */
export const prefetchGetBrainDumpIntroQuery = async <
  TData = Awaited<ReturnType<typeof getBrainDumpIntro>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpIntro>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetBrainDumpIntroQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type completeBrainDumpGreetingResponse200 = {
  data: GreetingDoneResponse;
  status: 200;
};

export type completeBrainDumpGreetingResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type completeBrainDumpGreetingResponseSuccess =
  completeBrainDumpGreetingResponse200 & {
    headers: Headers;
  };
export type completeBrainDumpGreetingResponseError =
  completeBrainDumpGreetingResponse401 & {
    headers: Headers;
  };

export type completeBrainDumpGreetingResponse =
  | completeBrainDumpGreetingResponseSuccess
  | completeBrainDumpGreetingResponseError;

export const getCompleteBrainDumpGreetingUrl = () => {
  return `/api/onboarding/brain-dump/intro/complete`;
};

/**
 * Mark the greeting as done — called when the first message is sent.
 *
 * Permanent and content-preserving: the flag flips, the stored greeting
 * stays in the row.
 * @summary Complete Brain Dump Greeting
 */
export const completeBrainDumpGreeting = async (
  options?: RequestInit,
): Promise<completeBrainDumpGreetingResponse> => {
  return customMutator<completeBrainDumpGreetingResponse>(
    getCompleteBrainDumpGreetingUrl(),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getCompleteBrainDumpGreetingMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof completeBrainDumpGreeting>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof completeBrainDumpGreeting>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["completeBrainDumpGreeting"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof completeBrainDumpGreeting>>,
    void
  > = () => {
    return completeBrainDumpGreeting(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CompleteBrainDumpGreetingMutationResult = NonNullable<
  Awaited<ReturnType<typeof completeBrainDumpGreeting>>
>;

export type CompleteBrainDumpGreetingMutationError =
  HTTP401NotAuthenticatedErrorResponse;

/**
 * @summary Complete Brain Dump Greeting
 */
export const useCompleteBrainDumpGreeting = <
  TError = HTTP401NotAuthenticatedErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof completeBrainDumpGreeting>>,
      TError,
      void,
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof completeBrainDumpGreeting>>,
  TError,
  void,
  TContext
> => {
  return useMutation(
    getCompleteBrainDumpGreetingMutationOptions(options),
    queryClient,
  );
};
export type uploadBrainDumpPartResponse200 = {
  data: UploadPartResponse;
  status: 200;
};

export type uploadBrainDumpPartResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type uploadBrainDumpPartResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type uploadBrainDumpPartResponseSuccess =
  uploadBrainDumpPartResponse200 & {
    headers: Headers;
  };
export type uploadBrainDumpPartResponseError = (
  | uploadBrainDumpPartResponse401
  | uploadBrainDumpPartResponse422
) & {
  headers: Headers;
};

export type uploadBrainDumpPartResponse =
  | uploadBrainDumpPartResponseSuccess
  | uploadBrainDumpPartResponseError;

export const getUploadBrainDumpPartUrl = () => {
  return `/api/onboarding/brain-dump/parts`;
};

/**
 * Accept one MediaRecorder timeslice blob.
 *
 * Parts arrive while the user is still talking, so this stays cheap:
 * size checks, buffer, done. Validation that needs the whole stream
 * (virus scan, transcription) happens on finalize.
 *
 * A part of a take the client discarded is refused, unless ``resent`` says
 * the client is deliberately sending that take again from its local copy.
 * @summary Upload Brain Dump Part
 */
export const uploadBrainDumpPart = async (
  bodyUploadBrainDumpPart: BodyUploadBrainDumpPart,
  options?: RequestInit,
): Promise<uploadBrainDumpPartResponse> => {
  const formData = new FormData();
  formData.append(`file`, bodyUploadBrainDumpPart.file);
  formData.append(`recording_id`, bodyUploadBrainDumpPart.recording_id);
  formData.append(`part_index`, bodyUploadBrainDumpPart.part_index.toString());
  if (bodyUploadBrainDumpPart.resent !== undefined) {
    formData.append(`resent`, bodyUploadBrainDumpPart.resent.toString());
  }

  return customMutator<uploadBrainDumpPartResponse>(
    getUploadBrainDumpPartUrl(),
    {
      ...options,
      method: "POST",
      body: formData,
    },
  );
};

export const getUploadBrainDumpPartMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadBrainDumpPart>>,
    TError,
    { data: BodyUploadBrainDumpPart },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadBrainDumpPart>>,
  TError,
  { data: BodyUploadBrainDumpPart },
  TContext
> => {
  const mutationKey = ["uploadBrainDumpPart"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadBrainDumpPart>>,
    { data: BodyUploadBrainDumpPart }
  > = (props) => {
    const { data } = props ?? {};

    return uploadBrainDumpPart(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadBrainDumpPartMutationResult = NonNullable<
  Awaited<ReturnType<typeof uploadBrainDumpPart>>
>;
export type UploadBrainDumpPartMutationBody = BodyUploadBrainDumpPart;
export type UploadBrainDumpPartMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Upload Brain Dump Part
 */
export const useUploadBrainDumpPart = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uploadBrainDumpPart>>,
      TError,
      { data: BodyUploadBrainDumpPart },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uploadBrainDumpPart>>,
  TError,
  { data: BodyUploadBrainDumpPart },
  TContext
> => {
  return useMutation(
    getUploadBrainDumpPartMutationOptions(options),
    queryClient,
  );
};
export type getBrainDumpRecommendedExpertsResponse200 = {
  data: RecommendedExpertsResponse;
  status: 200;
};

export type getBrainDumpRecommendedExpertsResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getBrainDumpRecommendedExpertsResponseSuccess =
  getBrainDumpRecommendedExpertsResponse200 & {
    headers: Headers;
  };
export type getBrainDumpRecommendedExpertsResponseError =
  getBrainDumpRecommendedExpertsResponse401 & {
    headers: Headers;
  };

export type getBrainDumpRecommendedExpertsResponse =
  | getBrainDumpRecommendedExpertsResponseSuccess
  | getBrainDumpRecommendedExpertsResponseError;

export const getGetBrainDumpRecommendedExpertsUrl = () => {
  return `/api/onboarding/brain-dump/recommended-experts`;
};

/**
 * The team Otto proposes: who to hire first, and the raise door.
 *
 * Written by a background job beside the greeting one; a plain read
 * here, with the deterministic fallback filled in for takes the job
 * never ran on. ``ready=false`` means keep polling. ``team`` is null
 * when the expert-team feature is off for this user.
 * @summary Get Brain Dump Recommended Experts
 */
export const getBrainDumpRecommendedExperts = async (
  options?: RequestInit,
): Promise<getBrainDumpRecommendedExpertsResponse> => {
  return customMutator<getBrainDumpRecommendedExpertsResponse>(
    getGetBrainDumpRecommendedExpertsUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetBrainDumpRecommendedExpertsQueryKey = () => {
  return [`/api/onboarding/brain-dump/recommended-experts`] as const;
};

export const getGetBrainDumpRecommendedExpertsQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBrainDumpRecommendedExpertsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>
  > = ({ signal }) =>
    getBrainDumpRecommendedExperts({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrainDumpRecommendedExpertsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>
>;
export type GetBrainDumpRecommendedExpertsQueryError =
  HTTP401NotAuthenticatedErrorResponse;

export function useGetBrainDumpRecommendedExperts<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpRecommendedExperts<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpRecommendedExperts<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Brain Dump Recommended Experts
 */

export function useGetBrainDumpRecommendedExperts<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrainDumpRecommendedExpertsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Brain Dump Recommended Experts
 */
export const prefetchGetBrainDumpRecommendedExpertsQuery = async <
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedExperts>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetBrainDumpRecommendedExpertsQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type getBrainDumpRecommendedProvidersResponse200 = {
  data: RecommendedProvidersResponse;
  status: 200;
};

export type getBrainDumpRecommendedProvidersResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getBrainDumpRecommendedProvidersResponseSuccess =
  getBrainDumpRecommendedProvidersResponse200 & {
    headers: Headers;
  };
export type getBrainDumpRecommendedProvidersResponseError =
  getBrainDumpRecommendedProvidersResponse401 & {
    headers: Headers;
  };

export type getBrainDumpRecommendedProvidersResponse =
  | getBrainDumpRecommendedProvidersResponseSuccess
  | getBrainDumpRecommendedProvidersResponseError;

export const getGetBrainDumpRecommendedProvidersUrl = () => {
  return `/api/onboarding/brain-dump/recommended-providers`;
};

/**
 * Provider picks for the welcome dialog's "Connect your tools" panel.
 *
 * Written by a background job that runs beside the greeting one; a plain
 * row read here. ``ready=false`` means keep polling.
 * @summary Get Brain Dump Recommended Providers
 */
export const getBrainDumpRecommendedProviders = async (
  options?: RequestInit,
): Promise<getBrainDumpRecommendedProvidersResponse> => {
  return customMutator<getBrainDumpRecommendedProvidersResponse>(
    getGetBrainDumpRecommendedProvidersUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetBrainDumpRecommendedProvidersQueryKey = () => {
  return [`/api/onboarding/brain-dump/recommended-providers`] as const;
};

export const getGetBrainDumpRecommendedProvidersQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBrainDumpRecommendedProvidersQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>
  > = ({ signal }) =>
    getBrainDumpRecommendedProviders({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrainDumpRecommendedProvidersQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>
>;
export type GetBrainDumpRecommendedProvidersQueryError =
  HTTP401NotAuthenticatedErrorResponse;

export function useGetBrainDumpRecommendedProviders<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpRecommendedProviders<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpRecommendedProviders<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Brain Dump Recommended Providers
 */

export function useGetBrainDumpRecommendedProviders<
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrainDumpRecommendedProvidersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Brain Dump Recommended Providers
 */
export const prefetchGetBrainDumpRecommendedProvidersQuery = async <
  TData = Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpRecommendedProviders>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetBrainDumpRecommendedProvidersQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type downloadBrainDumpRecordingResponse200 = {
  data: unknown;
  status: 200;
};

export type downloadBrainDumpRecordingResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type downloadBrainDumpRecordingResponseSuccess =
  downloadBrainDumpRecordingResponse200 & {
    headers: Headers;
  };
export type downloadBrainDumpRecordingResponseError =
  downloadBrainDumpRecordingResponse401 & {
    headers: Headers;
  };

export type downloadBrainDumpRecordingResponse =
  | downloadBrainDumpRecordingResponseSuccess
  | downloadBrainDumpRecordingResponseError;

export const getDownloadBrainDumpRecordingUrl = () => {
  return `/api/onboarding/brain-dump/recording`;
};

/**
 * Hand back the user's own recording, for as long as it is kept.
 *
 * Meant for a take that failed: "your recording is safe" has to be
 * true, and this is what makes it checkable. A transcribed take has no
 * recording left, since only the transcript is kept. A failed take's
 * recording is deleted within 48 hours (see ``retention.py``), and one
 * that is due is refused even before the sweep reaches it; a signed URL
 * for one that is gone would only fail at the bucket, so say so here
 * instead.
 * @summary Download Brain Dump Recording
 */
export const downloadBrainDumpRecording = async (
  options?: RequestInit,
): Promise<downloadBrainDumpRecordingResponse> => {
  return customMutator<downloadBrainDumpRecordingResponse>(
    getDownloadBrainDumpRecordingUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getDownloadBrainDumpRecordingQueryKey = () => {
  return [`/api/onboarding/brain-dump/recording`] as const;
};

export const getDownloadBrainDumpRecordingQueryOptions = <
  TData = Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getDownloadBrainDumpRecordingQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof downloadBrainDumpRecording>>
  > = ({ signal }) => downloadBrainDumpRecording({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DownloadBrainDumpRecordingQueryResult = NonNullable<
  Awaited<ReturnType<typeof downloadBrainDumpRecording>>
>;
export type DownloadBrainDumpRecordingQueryError =
  HTTP401NotAuthenticatedErrorResponse;

export function useDownloadBrainDumpRecording<
  TData = Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
          TError,
          Awaited<ReturnType<typeof downloadBrainDumpRecording>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useDownloadBrainDumpRecording<
  TData = Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
          TError,
          Awaited<ReturnType<typeof downloadBrainDumpRecording>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useDownloadBrainDumpRecording<
  TData = Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Download Brain Dump Recording
 */

export function useDownloadBrainDumpRecording<
  TData = Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getDownloadBrainDumpRecordingQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Download Brain Dump Recording
 */
export const prefetchDownloadBrainDumpRecordingQuery = async <
  TData = Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof downloadBrainDumpRecording>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDownloadBrainDumpRecordingQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type getBrainDumpStatusResponse200 = {
  data: DumpStatusResponse;
  status: 200;
};

export type getBrainDumpStatusResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getBrainDumpStatusResponseSuccess =
  getBrainDumpStatusResponse200 & {
    headers: Headers;
  };
export type getBrainDumpStatusResponseError = getBrainDumpStatusResponse401 & {
  headers: Headers;
};

export type getBrainDumpStatusResponse =
  | getBrainDumpStatusResponseSuccess
  | getBrainDumpStatusResponseError;

export const getGetBrainDumpStatusUrl = () => {
  return `/api/onboarding/brain-dump/status`;
};

/**
 * Polled by the loading screen while the pipeline runs.
 * @summary Get Brain Dump Status
 */
export const getBrainDumpStatus = async (
  options?: RequestInit,
): Promise<getBrainDumpStatusResponse> => {
  return customMutator<getBrainDumpStatusResponse>(getGetBrainDumpStatusUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetBrainDumpStatusQueryKey = () => {
  return [`/api/onboarding/brain-dump/status`] as const;
};

export const getGetBrainDumpStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrainDumpStatus>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getBrainDumpStatus>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBrainDumpStatusQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBrainDumpStatus>>
  > = ({ signal }) => getBrainDumpStatus({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrainDumpStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrainDumpStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrainDumpStatus>>
>;
export type GetBrainDumpStatusQueryError = HTTP401NotAuthenticatedErrorResponse;

export function useGetBrainDumpStatus<
  TData = Awaited<ReturnType<typeof getBrainDumpStatus>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpStatus>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpStatus>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpStatus<
  TData = Awaited<ReturnType<typeof getBrainDumpStatus>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpStatus>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpStatus>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpStatus<
  TData = Awaited<ReturnType<typeof getBrainDumpStatus>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Brain Dump Status
 */

export function useGetBrainDumpStatus<
  TData = Awaited<ReturnType<typeof getBrainDumpStatus>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrainDumpStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Brain Dump Status
 */
export const prefetchGetBrainDumpStatusQuery = async <
  TData = Awaited<ReturnType<typeof getBrainDumpStatus>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetBrainDumpStatusQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type getBrainDumpSuggestedTasksResponse200 = {
  data: SuggestedTasksResponse;
  status: 200;
};

export type getBrainDumpSuggestedTasksResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getBrainDumpSuggestedTasksResponseSuccess =
  getBrainDumpSuggestedTasksResponse200 & {
    headers: Headers;
  };
export type getBrainDumpSuggestedTasksResponseError =
  getBrainDumpSuggestedTasksResponse401 & {
    headers: Headers;
  };

export type getBrainDumpSuggestedTasksResponse =
  | getBrainDumpSuggestedTasksResponseSuccess
  | getBrainDumpSuggestedTasksResponseError;

export const getGetBrainDumpSuggestedTasksUrl = () => {
  return `/api/onboarding/brain-dump/suggested-tasks`;
};

/**
 * The greeting's suggested first tasks, each with its own state.
 *
 * Unlike ``/intro``, this keeps answering after the first message: the
 * list stays on Home, a sent row marked in progress, until every row is
 * dismissed.
 * @summary Get Brain Dump Suggested Tasks
 */
export const getBrainDumpSuggestedTasks = async (
  options?: RequestInit,
): Promise<getBrainDumpSuggestedTasksResponse> => {
  return customMutator<getBrainDumpSuggestedTasksResponse>(
    getGetBrainDumpSuggestedTasksUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetBrainDumpSuggestedTasksQueryKey = () => {
  return [`/api/onboarding/brain-dump/suggested-tasks`] as const;
};

export const getGetBrainDumpSuggestedTasksQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBrainDumpSuggestedTasksQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>
  > = ({ signal }) => getBrainDumpSuggestedTasks({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrainDumpSuggestedTasksQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>
>;
export type GetBrainDumpSuggestedTasksQueryError =
  HTTP401NotAuthenticatedErrorResponse;

export function useGetBrainDumpSuggestedTasks<
  TData = Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpSuggestedTasks<
  TData = Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
          TError,
          Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrainDumpSuggestedTasks<
  TData = Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Brain Dump Suggested Tasks
 */

export function useGetBrainDumpSuggestedTasks<
  TData = Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrainDumpSuggestedTasksQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Brain Dump Suggested Tasks
 */
export const prefetchGetBrainDumpSuggestedTasksQuery = async <
  TData = Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrainDumpSuggestedTasks>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetBrainDumpSuggestedTasksQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type updateBrainDumpSuggestedTasksResponse200 = {
  data: SuggestedTasksResponse;
  status: 200;
};

export type updateBrainDumpSuggestedTasksResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type updateBrainDumpSuggestedTasksResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type updateBrainDumpSuggestedTasksResponseSuccess =
  updateBrainDumpSuggestedTasksResponse200 & {
    headers: Headers;
  };
export type updateBrainDumpSuggestedTasksResponseError = (
  | updateBrainDumpSuggestedTasksResponse401
  | updateBrainDumpSuggestedTasksResponse422
) & {
  headers: Headers;
};

export type updateBrainDumpSuggestedTasksResponse =
  | updateBrainDumpSuggestedTasksResponseSuccess
  | updateBrainDumpSuggestedTasksResponseError;

export const getUpdateBrainDumpSuggestedTasksUrl = () => {
  return `/api/onboarding/brain-dump/suggested-tasks`;
};

/**
 * Mark suggested tasks sent, dismissed, or open again (Undo).
 *
 * Takes several ids so "Hide all" and its Undo are one write each.
 * @summary Update Brain Dump Suggested Tasks
 */
export const updateBrainDumpSuggestedTasks = async (
  suggestedTasksUpdate: SuggestedTasksUpdate,
  options?: RequestInit,
): Promise<updateBrainDumpSuggestedTasksResponse> => {
  return customMutator<updateBrainDumpSuggestedTasksResponse>(
    getUpdateBrainDumpSuggestedTasksUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(suggestedTasksUpdate),
    },
  );
};

export const getUpdateBrainDumpSuggestedTasksMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBrainDumpSuggestedTasks>>,
    TError,
    { data: SuggestedTasksUpdate },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBrainDumpSuggestedTasks>>,
  TError,
  { data: SuggestedTasksUpdate },
  TContext
> => {
  const mutationKey = ["updateBrainDumpSuggestedTasks"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBrainDumpSuggestedTasks>>,
    { data: SuggestedTasksUpdate }
  > = (props) => {
    const { data } = props ?? {};

    return updateBrainDumpSuggestedTasks(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBrainDumpSuggestedTasksMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBrainDumpSuggestedTasks>>
>;
export type UpdateBrainDumpSuggestedTasksMutationBody = SuggestedTasksUpdate;
export type UpdateBrainDumpSuggestedTasksMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Update Brain Dump Suggested Tasks
 */
export const useUpdateBrainDumpSuggestedTasks = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateBrainDumpSuggestedTasks>>,
      TError,
      { data: SuggestedTasksUpdate },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateBrainDumpSuggestedTasks>>,
  TError,
  { data: SuggestedTasksUpdate },
  TContext
> => {
  return useMutation(
    getUpdateBrainDumpSuggestedTasksMutationOptions(options),
    queryClient,
  );
};
